import Basemap from "@arcgis/core/Basemap";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import { useCallback, useEffect, useRef, useState } from "react";
import { applyLayerOrdering } from "../utils/API";
import { EMBED_VARIANTS } from "../utils/helper";

const useViewLayers = (view, config) => {
  let [localModule, setLocalModule] = useState();
  const effectTokenRef = useRef(0);

  useEffect(() => {
    if (localModule) return;
    let module = config.defaultModule;
    if (
      Array.isArray(config.activeModules) &&
      !config.activeModules.includes(module)
    ) {
      module = config.activeModules
        ? config.activeModules[0]
        : config.modules[0];
    }
    setLocalModule(module);
  }, [config]);

  const setLayerVisibility = useCallback(
    (layer, cancelRef) => {
      if (cancelRef?.current) {
        // Cancel before doing anything
        return;
      }
      if (layer.layerModules && layer.layerModules.length > 0 && localModule) {
        try {
          const alias = layer.layerConfig?.alias || layer.layerConfigAlias;

          const isInLayerModule =
            config.modules[localModule]?.layers?.includes(alias);

          const isInOperationalLayers =
            config.modules[localModule]?.optionalLayers?.includes(alias);

          const showLayer = isInLayerModule
            ? isInLayerModule
            : !isInOperationalLayers;

          layer.visible = layer.layerModules.includes(localModule) && showLayer;
        } catch (err) {
          console.log(err);
          layer.visible = false;
        }
      }
    },
    [localModule, config]
  );

  const handleMapLayersVisibility = useCallback(
    async (layers, effectToken) => {
      for (const layer of layers) {
        // Only proceed if this is the latest effect
        if (effectTokenRef.current !== effectToken) {
          break;
        }
        await new Promise((resolve) => {
          requestAnimationFrame(() => {
            setLayerVisibility(layer);
            resolve();
          });
        });
      }
    },
    [setLayerVisibility]
  );

  const waitForBasemapLoad = async (view) => {
    return new Promise((resolve) => {
      if (view.map.basemap.loaded) {
        resolve(true);
        return;
      }

      const watchHandle = reactiveUtils.watch(
        () => view.map.basemap.loaded,
        (isLoaded) => {
          if (isLoaded) {
            watchHandle.remove();
            requestAnimationFrame(() => {
              resolve(true);
            });
          }
        }
      );
    });
  };

  const hasBasemapChanged = useRef(false);
  const assignBasemap = useCallback(async (module) => {
    try {
      const moduleBasemapId = config.modules[module].basemapId;
      const moduleBasemap = config.modules[module].basemap;

      const configBasemapId = config.basemapId;
      const configBasemap = config.basemap;

      let mapBasemap = null;

      if (moduleBasemapId) {
        const isSame = configBasemapId === moduleBasemapId;
        if (!isSame) {
          mapBasemap = new Basemap({ portalItem: { id: moduleBasemapId } });
        }
      } else if (moduleBasemap) {
        const isSame = configBasemap === moduleBasemap;
        if (!isSame) {
          mapBasemap = Basemap.fromId(moduleBasemap);
        }
      }

      if (mapBasemap) {
        hasBasemapChanged.current = true;
        view.map.basemap = mapBasemap;
        return await waitForBasemapLoad(view);
      } else if (hasBasemapChanged.current) {
        hasBasemapChanged.current = false;
        view.map.basemap = configBasemapId
          ? new Basemap({ portalItem: { id: configBasemapId } })
          : Basemap.fromId(configBasemap);
        return await waitForBasemapLoad(view);
      }
      return true;
    } catch (err) {
      console.log(err);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!view || !localModule || !config.modules[localModule]) return;

    const effectToken = ++effectTokenRef.current;
    let layerChangeHandle = null;

    const changeViewConfigurations = async () => {
      try {
        const zoom = config.modules[localModule].zoom ?? config.zoom;
        const center = config.modules[localModule].center ?? config.center;
        const minZoom = config.modules[localModule].minZoom ?? config.minZoom;
        const maxZoom = config.modules[localModule].maxZoom ?? config.maxZoom;

        if (Number.isInteger(zoom) || Array.isArray(center)) {
          const targetZoom =
            zoom - (config.embed === EMBED_VARIANTS.WEBSITE ? 1 : 0);
          const targetCenter = center ?? view.center;
          await view.goTo({
            zoom: targetZoom,
            center: targetCenter,
          });
        }

        if (Number.isInteger(minZoom)) {
          view.constraints.minZoom = minZoom;
        }

        if (Number.isInteger(maxZoom)) {
          view.constraints.maxZoom = maxZoom;
        }

        await handleMapLayersVisibility(view.map.layers, effectToken);

        if (effectTokenRef.current === effectToken) {
          applyLayerOrdering({
            view,
            config,
          });
        }

        // Handle layer added event
        layerChangeHandle = view.map.layers.on("change", (event) => {
          if (event.added && effectTokenRef.current === effectToken) {
            handleMapLayersVisibility(event.added, effectToken);
          }
        });

        if (effectTokenRef.current === effectToken) {
          await assignBasemap(localModule);
        }
      } catch (err) {
        console.log(err);
      }
    };

    changeViewConfigurations();

    return () => {
      layerChangeHandle?.remove();
    };
  }, [localModule, config, view]);

  return { localModule, setLocalModule };
};

export default useViewLayers;
